import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { isEmpty } from 'ramda'
import {
  SMART_PLEX,
  SMART_SWITCH,
  HARDWIRED_COMPONENT,
  HARDWIRED_SWITCH,
} from 'constants/dash'
import {
  UNASSIGNED_PANEL_PART,
  STAGED_PANEL_PART,
} from 'components/SwitchAndGauge/definitions/dashConstants'
import strings from 'utils/strings'
import { capitalizeFirst, isEmptyString } from 'utils/utils'
import {
  FEATURE,
  SWITCH_ID,
  filterSchemaForAddRemoveFeatures,
  SWITCHES,
  NOTIFICATION_WARNING,
  LIST,
  LAYOUT_LOCATION_EDIT,
  DASH_VIEW,
  ADD,
  FAST_FEATURES,
  APPLY_FAST_FEATURES,
  ADD_REMOVE_FEATURES,
} from 'utils/constants'
import {
  getLocationNumericPosition,
  isValidSmartSwitchId,
  isStagedComponent,
  get42NPlugDisplayNumber,
  isTEMIndicator,
} from 'components/SwitchAndGauge/utils'
import { getAuthenticatedModulesForVehicleUpdate } from 'selectors/authorization/checPlus'
import {
  getGroupLabel,
  getComponent,
  getLocationDefinition,
  getIsSGNotesError,
  getIsPreProduction,
  getIsSAndGEditable,
  getDashVehicleSerialNumber,
  getVehicleSubType,
  getIsLocationOnDash,
  getIsM2Vehicle,
  getIsDashVehicleCHECP,
  getCanStagedComponents,
  getIsLeadPreProduction,
  getDependencyOrSwapoutsFromDetail,
  getShowMoveToStagingArea,
  hasStagedComponents,
  getSwitchesBySignalName,
  getAllSignalGroupsWithAlternatives,
} from 'selectors/dash'
import {
  setSwitchDetailsChanged,
  resetSwitchDetailsChanged,
  setUnassignedLocationData,
  addComponentToStaging,
  setLayoutLocationToUpdate,
  addSwitchToLocation,
} from 'actions/dash'
import {
  setSelectedIdWithDrawer,
  navigatorIsFullscreen,
  setSelectedIdWithDrawerAndMeta,
} from 'actions/navigator'
import { createAndAddFilterTermFromDash, startSearch } from 'actions/search'
import { createSystemNotification } from 'actions/application'
import {
  PRE_PROD_LOCATION_TYPE_UPDATE,
  PRE_PROD_LOCATION_TYPE_UPDATE_WARNING,
} from 'utils/notifications'
import {
  UPDATE_M2_SWITCH_LOCATION_DETAIL,
  UPDATE_PLUS_SWITCH_LOCATION_DETAIL,
  UPDATE_M2_SWITCH_CUSTOM_GRAPHIC,
  UPDATE_PLUS_SWITCH_CUSTOM_GRAPHIC,
  EDIT_DASHBOARD_LAYOUT,
  EDIT_PLUS_DASHBOARD_LAYOUT,
  ADD_SWITCH,
} from 'constants/authorization'
import { makeComponentMetaSelector } from 'selectors/navigator'
import { useSelector, useMakeSelector } from 'hooks/redux'
import CustomGraphicIcon from 'components/SwitchAndGauge/Dash/Part/CustomGraphicIcon'
import HardwiredSwitchIcon from 'components/SwitchAndGauge/Dash/Part/HardwiredSwitchIcon'
import { Button, Flex, Paragraph } from 'components/patterns'

import LinkedLogicsList from 'components/LinkedLogicsList/'

import HasRights from 'components/HasRights'
import Part from 'components/SwitchAndGauge/DashDrawer/PanelPartDetails/Part'
import CustomSwitchGraphicsModal from 'components/SwitchAndGauge/DashDrawer/CustomSwitchGraphicsModal'
import SelectLocationDetailsModal from 'components/SwitchAndGauge/DashDrawer/SelectLocationDetailsModal'
import ReplaceSwitchModal from 'components/SwitchAndGauge/DashDrawer/ReplaceSwitchModal'
import setTheme from 'theme'

import { UnAssignedPartHeaderIcon } from 'components/SwitchAndGauge/DashDrawer/DraggableList/DraggablePartListView.style'
import { hasRights } from 'selectors/authorization'
import {
  StyledHeader,
  StyledParagraphSmall,
  StyledPartDetails,
  StyledAlertIcon,
  ReplaceSwitchGraphicsContainer,
  ErrorIcon,
  ErrorLocation,
  StyledButton,
  StyledLocationContainer,
  InfoIcon,
  AccordionContainer,
  AccordionHeader,
  AccordionHeaderText,
  AccordionCaretSvg,
  AccordionContent,
  AccordionIconWrapper,
  AccordionRefreshIcon,
  AccordionBody,
  AccordionText,
  AccordionActions,
  TypeNameParagraph,
  TypeDescriptionParagraph,
} from './PanelPartDetails.style'

// PBI 147363 - Strip switch type suffix (e.g. "(Momentary)", "(Latching)") from signal name display
const stripSignalType = (name) =>
  name ? name.replace(/\s*\([^)]*\)\s*$/, '') : ''

const ERROR_CODE_01 = '01'
const ERROR_CODE_02 = '02'
const ERROR_CODE_09 = '09'
const COMPONENT_ERROR_MESSAGES = {
  [ERROR_CODE_01]: strings.unknown_location,
  [ERROR_CODE_02]: strings.panel_location_used,
  [ERROR_CODE_09]: strings.panel_location_blocked,
}

/* eslint-disable react-hooks/exhaustive-deps */
const PanelPartDetails = ({ className, hideLinkedLogics, partOverride }) => {
  const themes = setTheme()
  const { pathname } = useLocation()
  const isDashBuilderView = pathname.includes('/dash-layout/')
  const { id: location = '', view } = useMakeSelector(
    makeComponentMetaSelector,
    {
      id: DASH_VIEW,
    }
  )
  const dispatch = useDispatch()
  const [showCustomSwitchGraphicsModal, setShowCustomSwitchGraphicsModal] =
    useState(false)
  const [showLocationDetailsModal, setShowLocationDetailsModal] =
    useState(false)
  const [showReplaceSwitchModal, setShowReplaceSwitchModal] = useState(false)
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false)

  const serialNumber = useSelector(getDashVehicleSerialNumber)
  const component = useSelector(getComponent, location)
  const groupLabel = useSelector(getGroupLabel, location)
  const locationMeta = useSelector(getLocationDefinition, location)
  const isSGNotesError = useSelector(getIsSGNotesError)
  const isPreProdVehicle = useSelector(getIsPreProduction)
  const isLeadPreProdVehicle = useSelector(getIsLeadPreProduction)
  const isM2Vehicle = useSelector(getIsM2Vehicle)
  const isChecPlus = useSelector(getIsDashVehicleCHECP)
  const isEditable = useSelector(getIsSAndGEditable)
  const vehicleSubType = useSelector(getVehicleSubType)
  const isLocationOnDash = useSelector(getIsLocationOnDash, location)
  const canStagedComponents = useSelector(getCanStagedComponents)
  const { dependency } = useSelector(
    getDependencyOrSwapoutsFromDetail,
    locationMeta?.detail
  )
  const showMoveToStagingArea = useSelector(getShowMoveToStagingArea, location)
  const hasComponentStaged = useSelector(hasStagedComponents)
  const canAddSwitch = useSelector(hasRights, {
    rights: [ADD_SWITCH],
  })

  const renderLocation = () => {
    if (isStagedComponent(location)) {
      return (
        <StyledLocationContainer>
          <UnAssignedPartHeaderIcon
            width={30}
            height={20}
            icon="alert_exclamation_triangle_solid"
            fill={themes.badgeWarningColor}
          />
          <Paragraph size="small">
            {strings.staging_location_warning_info}
          </Paragraph>
        </StyledLocationContainer>
      )
    }

    return (
      <Paragraph>
        <span>{groupLabel} </span>
        <span>
          {strings.pos} {pos}
        </span>
      </Paragraph>
    )
  }

  const {
    panelNumber,
    panelDescription,
    dashObjectName,
    dashObjectDescription,
    signalName,
    isCustom: isCustomSwitch,
    isCustomIndicator,
    switchId,
    capPartNumber,
    capDescription,
    errorCode,
    errorLocation,
    type: partType,
    locationDetail,
    locationType,
    bom,
    isNative,
  } = partOverride || component || {}

  // On 42N vehicles, plugs are displayed with the standardised plug part number
  // to match the graphic and tooltip shown in the dashboard panel. The underlying
  // component identity (used for API calls) remains unchanged. (#485445)
  const displayedPanelNumber = get42NPlugDisplayNumber(
    panelNumber,
    vehicleSubType,
    panelDescription
  )

  // PBI 486774: Reset accordion when switching between different switches
  useEffect(() => {
    setIsAccordionExpanded(false)
  }, [panelNumber])

  // Get alternative switches with the same signal (PBI 147363)
  const alternativeSwitches = useSelector(getSwitchesBySignalName, {
    signalName,
    currentSwitchId: switchId,
  })
  const hasAlternativeSwitches = alternativeSwitches.length > 0

  // Get all signal groups with alternatives for multi-tab modal (PBI 147363)
  const allSignalGroups = useSelector(getAllSignalGroupsWithAlternatives)

  const handleReplaceSwitchGraphics = () => {
    setShowCustomSwitchGraphicsModal(true)
  }

  const startSearchCallback = useCallback(
    (collectionName) => {
      dispatch(
        startSearch(
          collectionName,
          collectionName === FAST_FEATURES
            ? '/features/search-fast-features'
            : 'features/search',
          {
            serial: serialNumber,
            filterConflicts: true,
          }
        )
      )
    },
    [dispatch, startSearch]
  )

  const handleAddRemoveFeature = (collectionName) => {
    const ID_LOOKUP = {
      [FAST_FEATURES]: APPLY_FAST_FEATURES,
      [FEATURE]: ADD_REMOVE_FEATURES,
    }
    const selectedId = ID_LOOKUP[collectionName] ?? switchId
    startSearchCallback(collectionName)
    dispatch(setSelectedIdWithDrawer(selectedId))
    dispatch(navigatorIsFullscreen(true))
    dispatch(
      createAndAddFilterTermFromDash(
        filterSchemaForAddRemoveFeatures[SWITCH_ID],
        switchId,
        SWITCHES,
        signalName,
        signalName
          .replace(' Up Request', '')
          .replace(' Down Request', '')
          .replace(/\s*\((?:Latching|Momentary)\)$/, ''),
        collectionName,
        serialNumber
      )
    )
  }

  const handleMoveToStaging = () => {
    dispatch(addComponentToStaging(location))
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: LIST,
      })
    )
  }

  const handleOnConfirm = () => {
    dispatch(setSwitchDetailsChanged())
    setShowCustomSwitchGraphicsModal(false)
  }

  const handleCustomGraphicsModalCancel = () => {
    setShowCustomSwitchGraphicsModal(false)
    dispatch(resetSwitchDetailsChanged())
  }

  const handleLocationDetailModalCancel = () => {
    setShowLocationDetailsModal(false)
  }

  // Replace Switch Modal handlers (PBI 147363)
  const handleToggleAccordion = () => {
    setIsAccordionExpanded((prev) => !prev)
  }

  const handleOpenReplaceSwitchModal = () => {
    setShowReplaceSwitchModal(true)
  }

  const handleReplaceSwitchModalCancel = () => {
    setShowReplaceSwitchModal(false)
  }

  // PBI 147363 - Doc 11: Replace switches with selections from multi-tab modal
  const handleReplaceSwitch = (selections) => {
    setShowReplaceSwitchModal(false)
    Object.entries(selections).forEach(
      ([selectedSignalName, selectedSwitch]) => {
        const group = allSignalGroups.find(
          (g) => g.signalName === selectedSignalName
        )
        if (group) {
          dispatch(
            addSwitchToLocation(
              selectedSwitch.panelNumber,
              group.currentLocation
            )
          )
        }
      }
    )
    dispatch(setSwitchDetailsChanged())
  }

  const handleLocationTypeSelect = (detail, type) => {
    dispatch(setUnassignedLocationData(location, detail, type))
    setShowLocationDetailsModal(false)
    dispatch(
      createSystemNotification({
        notificationMessage:
          strings.the_part_must_be_placed_on_the_dash_to_save_changes_to_its_location_and_type,
        notificationType: NOTIFICATION_WARNING,
        notificationId: PRE_PROD_LOCATION_TYPE_UPDATE_WARNING,
        persistent: true,
        showIcon: false,
        icon: 'alert_exclamation_triangle_solid',
        closable: true,
      })
    )
    dispatch(
      createSystemNotification({
        notificationMessage: strings.location_detail_and_type_have_been_updated,
        notificationId: PRE_PROD_LOCATION_TYPE_UPDATE,
        persistent: true,
        closable: true,
        icon: 'check-mark-only',
        showIcon: false,
      })
    )
  }

  const handleEditLocationClick = () => {
    // clear out any existing data
    dispatch(setLayoutLocationToUpdate(null))
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: LAYOUT_LOCATION_EDIT,
        id: location,
      })
    )
  }

  const authenticatedModules = useSelector(
    getAuthenticatedModulesForVehicleUpdate
  )

  const hasError = Boolean(errorCode)
  const isValidSwitchId = isValidSmartSwitchId(switchId)
  const pos = getLocationNumericPosition(location, panelNumber, vehicleSubType)
  const showLinkedLogics =
    !hideLinkedLogics && isValidSwitchId && partType !== HARDWIRED_SWITCH
  const isNotesErrorForCustom = isCustomSwitch && isSGNotesError

  const canAddLogic =
    !isEmpty(authenticatedModules) &&
    !isNative &&
    isValidSwitchId &&
    !isPreProdVehicle &&
    isChecPlus &&
    partType === SMART_SWITCH &&
    !isDashBuilderView &&
    view !== ADD

  const showNativeSwitchMessage = isNative && !canAddLogic && !isM2Vehicle

  const createErrorMessage = () => {
    const errorMessage = locationDetail
      ? `${strings.no_location_for} ${locationDetail} ${locationType}`
      : COMPONENT_ERROR_MESSAGES[errorCode]

    return (
      <>
        <Paragraph>
          <ErrorIcon
            icon="alert_exclamation_triangle_solid"
            fill="warningPrimary"
          />
          {errorLocation && <ErrorLocation> {errorLocation}</ErrorLocation>}
          <span> {errorMessage}</span>
        </Paragraph>
        {isPreProdVehicle && isEditable && errorCode === ERROR_CODE_01 && (
          <HasRights
            rights={[
              UPDATE_M2_SWITCH_LOCATION_DETAIL,
              UPDATE_PLUS_SWITCH_LOCATION_DETAIL,
            ]}
          >
            <Button
              type="primary"
              onClick={() => setShowLocationDetailsModal(true)}
            >
              {!locationDetail && strings.select_location_details_and_type}
              {!!locationDetail && strings.change_location_detail_and_type}
            </Button>
          </HasRights>
        )}
      </>
    )
  }

  const handleAddSwitch = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: ADD,
        id: location,
      })
    )
  }

  const isCustomHardWiredSwitch = partType === HARDWIRED_SWITCH && capPartNumber

  const showCustomSwitchGraphicsBtn =
    (isCustomSwitch || isCustomIndicator || isCustomHardWiredSwitch) &&
    !isPreProdVehicle
  const showStagingAreaBtn =
    isLeadPreProdVehicle &&
    isLocationOnDash &&
    !isStagedComponent(location) &&
    canStagedComponents

  const showAddaSwitchBtn =
    !isPreProdVehicle &&
    canAddSwitch &&
    !panelNumber &&
    view !== ADD &&
    isChecPlus

  return (
    <StyledPartDetails className={className}>
      {(displayedPanelNumber || dashObjectName) && (
        <StyledHeader type={'h3'}>
          {!isEmptyString(displayedPanelNumber)
            ? displayedPanelNumber
            : dashObjectName}
        </StyledHeader>
      )}
      {showAddaSwitchBtn ? (
        <Flex gap="18px" alignItems="center">
          <Part
            partOverride={partOverride}
            location={location}
            dependency={dependency}
            isCustom={isCustomSwitch || isCustomIndicator}
            isHardwiredSwitch={partType === HARDWIRED_SWITCH}
            isCustomHardWiredSwitch={isCustomHardWiredSwitch}
          />
          <Button type="secondary" onClick={handleAddSwitch}>
            {strings.add_a_switch}
          </Button>
        </Flex>
      ) : (
        <Part
          partOverride={partOverride}
          location={location}
          dependency={dependency}
          isCustom={isCustomSwitch || isCustomIndicator}
          isHardwiredSwitch={partType === HARDWIRED_SWITCH}
          isCustomHardWiredSwitch={isCustomHardWiredSwitch}
        />
      )}
      {showCustomSwitchGraphicsBtn && (
        <HasRights
          rights={[
            UPDATE_PLUS_SWITCH_CUSTOM_GRAPHIC,
            UPDATE_M2_SWITCH_CUSTOM_GRAPHIC,
          ]}
        >
          <ReplaceSwitchGraphicsContainer>
            <Button type="secondary" onClick={handleReplaceSwitchGraphics}>
              {isCustomIndicator
                ? strings.replace_tem_indicator_graphic
                : strings.replace_switch_graphic}
            </Button>
          </ReplaceSwitchGraphicsContainer>
        </HasRights>
      )}
      <div>
        {isNotesErrorForCustom && (
          <StyledParagraphSmall size="small" weight="bold">
            <StyledAlertIcon
              icon="alert_exclamation_triangle_solid"
              fill="warningPrimary"
            />
            {strings.custom_switch_notes_error}
          </StyledParagraphSmall>
        )}
        {(panelDescription || dashObjectDescription) && (
          <>
            <StyledParagraphSmall size="small" weight="bold">
              {strings.description}
            </StyledParagraphSmall>
            <Paragraph>
              {!isEmptyString(panelDescription)
                ? panelDescription
                : dashObjectDescription}
            </Paragraph>
          </>
        )}
        {signalName && (
          <>
            <StyledParagraphSmall size="small" weight="bold">
              {strings.logic_signal}
            </StyledParagraphSmall>
            <Paragraph>{stripSignalType(signalName)}</Paragraph>
            {/* Contextual Help Accordion for Replace Switch (PBI 147363) */}
            {hasAlternativeSwitches && !isPreProdVehicle && isChecPlus && (
              <AccordionContainer>
                <AccordionHeader
                  onClick={handleToggleAccordion}
                  role="button"
                  tabIndex={0}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleToggleAccordion()
                    }
                  }}
                  aria-expanded={isAccordionExpanded}
                >
                  <AccordionHeaderText>
                    {strings.would_you_like_to_swap_smart_switch}
                  </AccordionHeaderText>
                  <AccordionCaretSvg
                    xmlns="http://www.w3.org/2000/svg"
                    width="10"
                    height="5"
                    viewBox="0 0 10 5"
                    fill="none"
                    isExpanded={isAccordionExpanded}
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M0 0L5 5L10 0H0Z"
                      fill="#6C18BA"
                    />
                  </AccordionCaretSvg>
                </AccordionHeader>
                {isAccordionExpanded && (
                  <AccordionContent>
                    <AccordionIconWrapper>
                      <AccordionRefreshIcon
                        icon="refresh"
                        fill="whitePrimary"
                      />
                    </AccordionIconWrapper>
                    <AccordionBody>
                      <AccordionText>
                        {strings.several_smart_switches_can_use_logic_input}
                      </AccordionText>
                      <AccordionActions>
                        <Button
                          type="secondary"
                          onClick={handleOpenReplaceSwitchModal}
                        >
                          {strings.update_switch}
                        </Button>
                      </AccordionActions>
                    </AccordionBody>
                  </AccordionContent>
                )}
              </AccordionContainer>
            )}
          </>
        )}
        <StyledParagraphSmall size="small" weight="bold">
          {strings.location}
        </StyledParagraphSmall>
        {hasError ? createErrorMessage() : renderLocation()}
        <HasRights
          rights={[EDIT_DASHBOARD_LAYOUT, EDIT_PLUS_DASHBOARD_LAYOUT]}
          modifiers={
            !location.includes(UNASSIGNED_PANEL_PART) &&
            !location.includes(STAGED_PANEL_PART)
          }
        >
          <Button
            type="secondary"
            onClick={handleEditLocationClick}
            disabled={hasComponentStaged}
          >
            {strings.edit_this_location}
          </Button>
        </HasRights>
        {showStagingAreaBtn &&
          (showMoveToStagingArea.canStage ? (
            <>
              <StyledParagraphSmall size="small">
                {strings.put_part_in_staging_area_message}
              </StyledParagraphSmall>
              <StyledButton type="secondary" onClick={handleMoveToStaging}>
                {strings.move_to_the_staging_area}
              </StyledButton>
            </>
          ) : (
            <>
              {showMoveToStagingArea.reason && (
                <StyledLocationContainer>
                  <InfoIcon
                    icon="alert_info_i_round_solid"
                    width={24}
                    fill="slateSecondary"
                  />
                  <StyledParagraphSmall size="small">
                    {showMoveToStagingArea.reason}
                  </StyledParagraphSmall>
                </StyledLocationContainer>
              )}
            </>
          ))}
        {Boolean(bom) && (
          <>
            <StyledParagraphSmall size="small" weight="bold">
              {strings.bom_title}
            </StyledParagraphSmall>
            <Paragraph>{bom}</Paragraph>
          </>
        )}
        {capPartNumber && !isEmpty(locationMeta) && (
          <>
            <StyledParagraphSmall size="small" weight="bold">
              {strings.cap_part_no}
            </StyledParagraphSmall>
            <Paragraph>{capPartNumber}</Paragraph>
            <StyledParagraphSmall size="small" weight="bold">
              {strings.cap_description}
            </StyledParagraphSmall>
            <Paragraph>{capDescription}</Paragraph>
          </>
        )}
        {switchId && (
          <>
            <StyledParagraphSmall size="small" weight="bold">
              {strings.switch_id}
            </StyledParagraphSmall>
            <Paragraph>{switchId}</Paragraph>
          </>
        )}
        {partTypeSection(
          partType,
          isCustomSwitch,
          isCustomIndicator,
          isCustomHardWiredSwitch,
          panelNumber
        )}
        {showLinkedLogics && (
          <LinkedLogicsList
            associatedControl="switch"
            switchId={switchId}
            isNativeSwitch={showNativeSwitchMessage}
          />
        )}
        {canAddLogic && (
          <>
            <StyledParagraphSmall
              style={{ marginBottom: '0px' }}
              size="small"
              weight="bold"
            >
              {strings.add_features}
            </StyledParagraphSmall>
            <Flex direction="row" gap="8px">
              <StyledButton
                type="secondary"
                style={{ marginBottom: '4px', marginTop: '4px' }}
                onClick={() => handleAddRemoveFeature(FEATURE)}
              >
                {strings.add_individual_features}
              </StyledButton>
              {isCustomSwitch && (
                <StyledButton
                  type="secondary"
                  style={{ marginBottom: '4px', marginTop: '4px' }}
                  onClick={() => handleAddRemoveFeature(FAST_FEATURES)}
                >
                  {strings.use_fast_features}
                </StyledButton>
              )}
            </Flex>
          </>
        )}
      </div>
      {showCustomSwitchGraphicsModal && (
        <CustomSwitchGraphicsModal
          isOpen={showCustomSwitchGraphicsModal}
          panelNumber={panelNumber}
          onClickConfirm={handleOnConfirm}
          onClickCancel={handleCustomGraphicsModalCancel}
          isCustomIndicator={isCustomIndicator}
        />
      )}
      {showLocationDetailsModal && (
        <SelectLocationDetailsModal
          isOpen={showLocationDetailsModal}
          panelNumber={panelNumber}
          onClickSelect={handleLocationTypeSelect}
          onClickCancel={handleLocationDetailModalCancel}
        />
      )}
      {showReplaceSwitchModal && (
        <ReplaceSwitchModal
          isOpen={showReplaceSwitchModal}
          signalGroups={allSignalGroups.filter(
            (g) => g.signalName === signalName
          )}
          initialSignalName={signalName}
          onReplace={handleReplaceSwitch}
          onCancel={handleReplaceSwitchModalCancel}
        />
      )}
    </StyledPartDetails>
  )
}

PanelPartDetails.propTypes = {
  className: PropTypes.string,
  hideLinkedLogics: PropTypes.bool,
  partOverride: PropTypes.object,
}
export default PanelPartDetails

const partTypeSection = (
  type,
  isCustomSwitch,
  isCustomIndicator,
  isCustomHardWiredSwitch,
  panelNumber
) => {
  const isCustomTEMIndicator = isCustomIndicator && !isTEMIndicator(panelNumber)
  const customLabel =
    isCustomSwitch || isCustomTEMIndicator ? (
      <span>
        <CustomGraphicIcon
          style={{ position: 'relative', top: 5, width: 18, marginRight: 8 }}
        />
        {`${strings.custom} `}
      </span>
    ) : null
  const customPrefix =
    isCustomSwitch || isCustomTEMIndicator ? `${strings.custom} ` : ''
  switch (type) {
    case SMART_PLEX: {
      const movementTypeName = `${customPrefix}${strings.smart_plex_switch}`
      return (
        <TypeLabel>
          <TypeNameParagraph>
            {customLabel}
            {strings.smart_plex_switch}
          </TypeNameParagraph>
          <TypeDescriptionParagraph>{`This ${movementTypeName} ${strings.smart_switch_movement_suffix}`}</TypeDescriptionParagraph>
        </TypeLabel>
      )
    }

    case SMART_SWITCH: {
      const label =
        isCustomIndicator === true
          ? strings.tem_indicator
          : strings.smart_switch
      const movementTypeName = `${customPrefix}${label}`
      return (
        <TypeLabel>
          <TypeNameParagraph>
            {customLabel} {label}
          </TypeNameParagraph>
          <TypeDescriptionParagraph>{`This ${movementTypeName} ${strings.smart_switch_movement_suffix}`}</TypeDescriptionParagraph>
        </TypeLabel>
      )
    }

    case HARDWIRED_SWITCH: {
      const movementTypeName = isCustomHardWiredSwitch
        ? strings.custom_hardwired_switch
        : strings.hardwired_switch
      return (
        <TypeLabel>
          <TypeNameParagraph>
            {customLabel}
            {isCustomHardWiredSwitch ? (
              <>
                <CustomGraphicIcon
                  style={{
                    position: 'relative',
                    top: 5,
                    width: 18,
                    marginRight: 8,
                  }}
                />
                <HardwiredSwitchIcon
                  style={{
                    position: 'relative',
                    top: 5,
                    width: 18,
                    marginRight: 8,
                  }}
                />
              </>
            ) : (
              <HardwiredSwitchIcon
                style={{
                  position: 'relative',
                  top: 5,
                  width: 18,
                  marginRight: 8,
                }}
              />
            )}

            {movementTypeName}
          </TypeNameParagraph>
          <TypeDescriptionParagraph>{`This ${movementTypeName} ${strings.hardwired_switch_movement_suffix}`}</TypeDescriptionParagraph>
        </TypeLabel>
      )
    }

    case HARDWIRED_COMPONENT: {
      return (
        <TypeLabel>
          <TypeNameParagraph>{strings.hardwired_component}</TypeNameParagraph>
          <TypeDescriptionParagraph>{`This ${strings.hardwired_component} ${strings.hardwired_component_movement_suffix}`}</TypeDescriptionParagraph>
        </TypeLabel>
      )
    }

    default: {
      return null
    }
  }
}

/* eslint react/prop-types: 0 */
const TypeLabel = ({ children }) => (
  <>
    <StyledParagraphSmall size="small" weight="bold">
      {capitalizeFirst(strings.type)}
    </StyledParagraphSmall>
    {children}
  </>
)
